import { useState } from 'react'
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import { X } from 'lucide-react'
import { usePlotComments, useSession } from '../../api/hooks'
import type { PlotComment } from '../../api/types'
import { Eyebrow } from '../../components/primitives'
import { formatDateTime } from '../../lib/format'
import { PersonName } from '../../lib/people'
import { tokens } from '../../theme'

/**
 * Reviewers' notes on a plot: why it was excluded, what the supplier said,
 * what to look at next time. Oldest first, each with who wrote it and when;
 * an author can take their own back. Saved on its own, apart from the
 * attribute edits, so writing one never moves the review on.
 */
export function PlotComments({
  plotId, runId, comments,
}: { plotId: string; runId: string | undefined; comments: PlotComment[] }) {
  const { data: me } = useSession()
  const { add, remove } = usePlotComments(plotId, runId)
  const [draft, setDraft] = useState('')
  const text = draft.trim()

  const submit = () => {
    if (!text || add.isPending) return
    add.mutate(text, { onSuccess: () => setDraft('') })
  }

  return (
    // Inside the panel's scrolling attributes block, which has the padding.
    <Box sx={{ mt: 4, pt: 4, borderTop: `1px solid ${tokens.color.ink200}`, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Eyebrow>Comments{comments.length ? ` · ${comments.length}` : ''}</Eyebrow>

      {comments.map((c) => (
        <Box key={c.id} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              <PersonName id={c.author_id} fallback={c.author ?? 'Former user'} /> · {formatDateTime(c.created_at)}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 0.5 }}>
              {c.body}
            </Typography>
          </Box>
          {c.author_id && c.author_id === me?.id && (
            <Tooltip title="Delete your comment">
              <span>
                <IconButton
                  size="small"
                  aria-label="Delete comment"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(c.id)}
                >
                  <X size={14} strokeWidth={1.5} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      ))}

      <TextField
        multiline
        minRows={2}
        maxRows={8}
        fullWidth
        size="small"
        value={draft}
        placeholder="Add a comment for whoever reviews this plot next"
        inputProps={{ 'aria-label': 'Comment', maxLength: 4000 }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          // Ctrl/Cmd+Enter sends; Enter alone is a new line.
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit()
        }}
        // The field draws its own focus border; the global focus ring on the
        // textarea inside it made a second one.
        sx={{ '& textarea': { fontSize: 13, px: 0 }, '& textarea:focus-visible': { boxShadow: 'none' } }}
      />
      {add.isError && (
        <Typography variant="caption" sx={{ color: tokens.color.danger }}>
          {(add.error as Error).message}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" variant="outlined" disabled={!text || add.isPending} onClick={submit}>
          {add.isPending ? 'Adding…' : 'Add comment'}
        </Button>
      </Box>
    </Box>
  )
}
